import { useState, type FormEvent } from 'react';
import { MultiCloudFlowVisualization } from '@/components/flow/MultiCloudFlowVisualization';

/**
 * Home Page
 * Pathfinder-style layout adapted for Naivana.
 */

export function HomePage() {
  const [betaEmail, setBetaEmail] = useState('');
  const [betaCompany, setBetaCompany] = useState('');
  const [betaStatus, setBetaStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleBetaSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (betaStatus === 'submitting') return;

    setBetaStatus('submitting');

    try {
      const response = await fetch('https://contact.naivana.io/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'Beta Signup',
          email: betaEmail,
          company: betaCompany,
          message: 'Requesting beta access from landing page.',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setBetaStatus('success');
      setBetaEmail('');
      setBetaCompany('');
    } catch (error) {
      setBetaStatus('error');
    }
  };

  return (
    <>
      <section className="hero">
        <div className="hero-content">
          <div className="beta-badge">⚡ Private Beta - 50 Spots Available</div>
          <h1>
            Cut your AI bill <span className="highlight">40%</span>. Zero code changes.
          </h1>
          <p>
            Drop-in proxy. Routes to the cheapest capable model. Caches repeated queries. Compresses
            long prompts. Saves 30–50% on average.
          </p>
          <p className="hero-note">
            Built for seed-to-Series-A teams burning $5K–$50K/month on AI.
          </p>

          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">35%</span>
              <span className="stat-label">Average cost reduction</span>
            </div>
            <div className="stat">
              <span className="stat-number">500K</span>
              <span className="stat-label">Requests routed daily</span>
            </div>
            <div className="stat">
              <span className="stat-number">&lt;150ms</span>
              <span className="stat-label">Added latency (p95)</span>
            </div>
          </div>

          <div className="cta-buttons">
            <a href="#join-beta" className="btn btn-primary">
              Join Beta (Free)
            </a>
            <a href="#how-it-works" className="btn btn-secondary">
              See How It Works
            </a>
          </div>
          <p className="hero-urgency">47 teams on the waitlist. 3 spots left this week.</p>

          <div className="hero-visual">
            <div className="hero-visual-card">
              <div className="hero-visual-header">
                <span className="hero-visual-eyebrow">Routing example</span>
                <h3>Multi-cloud routing</h3>
                <p>Intelligent load balancing with automatic failover across providers.</p>
              </div>
              <MultiCloudFlowVisualization autoAnimate cycleInterval={3500} />
            </div>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="section-header">
          <h2>You&apos;re Wasting Money on AI</h2>
          <p>
            Most founders route every request to GPT-5.2 or Claude Opus 4.5. That&apos;s like taking
            a Ferrari to get groceries.
          </p>
        </div>

        <div className="problem-grid">
          <div className="problem-card">
            <span className="problem-stat">70%</span>
            <h3>of your requests</h3>
            <p>
              could be handled by cheaper models like Gemini 3 Flash, saving you thousands per
              month.
            </p>
          </div>

          <div className="problem-card">
            <span className="problem-stat">$15K+</span>
            <h3>average monthly waste</h3>
            <p>
              from routing simple queries to expensive flagship models. That&apos;s $180K/year you
              could invest in growth.
            </p>
          </div>

          <div className="problem-card">
            <span className="problem-stat">0</span>
            <h3>optimization effort</h3>
            <p>
              Because you&apos;re too busy building your product to manually optimize every AI call.
              We get it.
            </p>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="how-it-works">
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Drop-in replacement for your existing AI provider. Literally 3 lines of code.</p>
        </div>

        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Drop In</h3>
            <p>Replace your OpenAI/Anthropic endpoint with Naivana. Keep your existing code.</p>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <h3>We Route</h3>
            <p>Our routing layer analyzes each request and routes it to the optimal provider.</p>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <h3>You Save</h3>
            <p>Same quality responses, 30-40% lower costs. Watch the savings compound.</p>
          </div>
        </div>

        <div className="code-example">
          <pre>
            <code>
              <span className="code-comment"># Before: Expensive default routing</span>
              {'\n'}import openai{'\n'}response = openai.chat.completions.create({'\n'}    model=
              &quot;gpt-5.2&quot;,{' '}
              <span className="code-comment"># Always expensive</span>
              {'\n'}    messages=[{'{'}&quot;role&quot;: &quot;user&quot;, &quot;content&quot;: prompt{'}'}]
              {'\n'}){'\n'}
              {'\n'}
              <span className="code-comment"># After: Smart routing with Naivana</span>
              {'\n'}import naivana{'\n'}response = naivana.chat.completions.create({'\n'}   {' '}
              <span className="code-comment"># No model specified - we pick the best one</span>
              {'\n'}    messages=[{'{'}&quot;role&quot;: &quot;user&quot;, &quot;content&quot;: prompt{'}'}]
              {'\n'}){'\n'}
              <span className="code-comment"># Simple queries → Gemini 3 Flash</span>
              {'\n'}
              <span className="code-comment"># High-stakes queries → Claude Opus 4.5</span>
              {'\n'}
              <span className="code-comment">
                # Decision engine balances cost, latency, and accuracy
              </span>
              {'\n'}
              <span className="code-comment"># Result: 35% average savings</span>
            </code>
          </pre>
        </div>
      </section>

      <section id="pricing" className="pricing">
        <div className="section-header">
          <h2>Pricing That Scales With You</h2>
          <p>
            Provider token prices pass through. We add a 5.5% platform fee on usage.
          </p>
        </div>

        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>Spark</h3>
            <div className="price">$0</div>
            <div className="price-period">/month + 5.5% usage fee</div>
            <ul className="pricing-features">
              <li>50K requests/month</li>
              <li>Basic routing, 3 providers</li>
              <li>Routing only — upgrade to unlock caching + compression</li>
              <li>Community support</li>
            </ul>
            <a href="#join-beta" className="btn btn-primary">
              Start Spark
            </a>
          </div>

          <div className="pricing-card">
            <h3>Launch</h3>
            <div className="price">$19</div>
            <div className="price-period">/month + 5.5% usage fee</div>
            <ul className="pricing-features">
              <li>200K requests/month</li>
              <li>5K cached responses/mo</li>
              <li>10K compressed requests/mo</li>
              <li>Analytics + usage alerts</li>
              <li>Email support</li>
            </ul>
            <a href="#join-beta" className="btn btn-secondary">
              Start Launch
            </a>
          </div>

          <div className="pricing-card featured">
            <h3>Scale</h3>
            <div className="price">$49</div>
            <div className="price-period">/month + 5.5% usage fee</div>
            <ul className="pricing-features">
              <li>2M requests/month</li>
              <li>50K cached responses/mo</li>
              <li>Unlimited compression</li>
              <li>Advanced routing policies</li>
              <li>Cost reports + team access</li>
            </ul>
            <a href="#join-beta" className="btn btn-secondary">
              Start Scale
            </a>
          </div>

          <div className="pricing-card">
            <h3>Enterprise</h3>
            <div className="price">Custom</div>
            <div className="price-period">5.5% usage fee</div>
            <ul className="pricing-features">
              <li>Unlimited requests</li>
              <li>Unlimited caching + compression</li>
              <li>SLA + dedicated support</li>
              <li>Custom routing policies</li>
              <li>Private deployments</li>
            </ul>
            <a href="mailto:carlton@naivana.co" className="btn btn-secondary">
              Contact Sales
            </a>
          </div>
        </div>

        <div
          style={{
            textAlign: 'center',
            marginTop: '3rem',
            padding: '2rem',
            background: 'var(--gray-light)',
            borderRadius: '12px',
          }}
        >
          <p style={{ fontSize: '1.125rem' }}>
            <strong>ROI Guarantee:</strong> If you don&apos;t save at least your monthly subscription
            cost, we&apos;ll refund the difference. No questions asked.
          </p>
          <p style={{ fontSize: '0.875rem', opacity: 0.7, marginTop: '0.75rem' }}>
            Overage model: caching/compression continue beyond tier limits with per-request fees.
          </p>
          <p style={{ fontSize: '0.875rem', opacity: 0.7, marginTop: '0.75rem' }}>
            *Spark limits can be increased for incorporated teams (EIN + live site required). Tell
            us what you&apos;re building.
          </p>
        </div>
      </section>

      <section id="technical" className="technical">
        <div className="section-header">
          <h2>Built for Engineers, By Engineers</h2>
          <p>Privacy-first. Production-ready. Battle-tested with 500K daily requests.</p>
        </div>

        <div className="tech-grid">
          <div className="tech-card">
            <h3>🔒 Privacy First</h3>
            <p>
              We NEVER store your prompts. Only SHA-256 hashes for routing optimization. GDPR/SOC2
              compliant architecture.
            </p>
          </div>

          <div className="tech-card">
            <h3>🚀 Low Latency</h3>
            <p>&lt;150ms added latency (p95). Aggressive caching. Parallel provider health checks.</p>
          </div>

          <div className="tech-card">
            <h3>🧠 ML-Powered</h3>
            <p>
              Routing uses live cost, latency, and quality signals to pick the best available model
              for each request.
            </p>
          </div>

          <div className="tech-card">
            <h3>🔄 Multi-Provider</h3>
            <p>
              OpenAI, Anthropic, Google Gemini. Automatic failover. No vendor lock-in, so you can
              use the models you choose instead of a single cloud’s catalog.
            </p>
          </div>

          <div className="tech-card">
            <h3>📊 Full Observability</h3>
            <p>Real-time analytics. Cost breakdowns. Provider performance. Exportable reports.</p>
          </div>

          <div className="tech-card">
            <h3>⚡ Drop-In Integration</h3>
            <p>OpenAI-compatible API. TypeScript/Python SDKs. Works with your existing stack.</p>
          </div>
        </div>
      </section>

      <section id="join-beta" className="final-cta">
        <h2>Join 50+ Founders Already Saving</h2>
        <p>Beta spots are limited. Get in now, lock in free access, and start cutting costs.</p>

        <div style={{ maxWidth: '500px', margin: '2rem auto' }}>
          <form
            id="beta-form"
            onSubmit={handleBetaSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          >
            <input
              type="email"
              placeholder="your@email.com"
              required
              value={betaEmail}
              onChange={(event) => setBetaEmail(event.target.value)}
              style={{
                padding: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                borderRadius: '8px',
                fontSize: '1rem',
                background: 'rgba(255,255,255,0.9)',
                color: '#2f2f2f',
              }}
            />
            <input
              type="text"
              placeholder="Company name (optional)"
              value={betaCompany}
              onChange={(event) => setBetaCompany(event.target.value)}
              style={{
                padding: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                borderRadius: '8px',
                fontSize: '1rem',
                background: 'rgba(255,255,255,0.9)',
                color: '#2f2f2f',
              }}
            />
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', opacity: betaStatus === 'submitting' ? 0.8 : 1 }}
              disabled={betaStatus === 'submitting'}
            >
              {betaStatus === 'submitting' ? 'Submitting…' : 'Get Beta Access →'}
            </button>
            {betaStatus === 'success' && (
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Thanks! We&apos;ll follow up with access details soon.
              </p>
            )}
            {betaStatus === 'error' && (
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Something went wrong. Please try again.
              </p>
            )}
            <p style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: '0.5rem' }}>
              Billing required for usage-based token charges and platform fees.
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
